import { isEmpty, isFunction, get } from 'underscore';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withScope } from '@bingads-webui-react/scope-service';
import { Alert } from '@bingads-webui-react/alert';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getProductData, getProductScopeFromOData, getStores, getCountries } from './utils';
import { getStoreStyles } from '../styles/get-store-style';

export const StoreSelectLoading = ({
  i18n, legacyI18n, scope, campaignObjective, updateValidation,
  validationProps: { data, alwaysShowError },
  isLoading, onLoadingDone, onLoadingError, loadingErrors, permissions, onRenderCustomLoadingComponent, serverContext, activity,
}) => {
  const asyncTokenRef = useRef();
  const {
    IsDomesticSimplifiedChineseEnabled,
  } = permissions || {};
  const isOdataGetProductScopeEnabled = get(serverContext, ['DynamicPermissions', 'OdataGetProductScope'], false);

  const isUnmounted = useRef(false);
  useEffect(() => () => {
    isUnmounted.current = true;
    if (asyncTokenRef.current) {
      asyncTokenRef.current.cancel();
    }
  }, []);

  useEffect(() => {
    const errors = [
      ...isLoading && campaignObjective === 'ShoppingCampaign' ?
        [i18n.getString(_TL_('Cannot continue until stores are loaded.'))] : [],
      ...!isEmpty(loadingErrors) && campaignObjective === 'ShoppingCampaign' ?
        [i18n.getString(_TL_('Cannot continue because stores failed to load. Please try again or contact the customer support.'))] : [],
    ];
    updateValidation(errors, data);
  }, [i18n, campaignObjective, updateValidation, data, isLoading, loadingErrors]);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    if (asyncTokenRef.current) {
      asyncTokenRef.current.cancel();
    }
    const { cid, aid, uid } = scope.values;
    const getProductDataCall = isOdataGetProductScopeEnabled ? () => getProductScopeFromOData(serverContext) : () => getProductData(cid, aid, uid);
    const callStartTime = Date.now();

    asyncTokenRef.current = getProductDataCall()
      .on({
        onSuccess: (productData) => {
          const deltaTime = Date.now() - callStartTime;
          if (isOdataGetProductScopeEnabled) {
            activity.trace(`[PmaxStoreSelect][OdataGetProductScope] GetProductDataCallTime: ${deltaTime}`);
          } else {
            activity.trace(`[PmaxStoreSelect] GetProductDataCallTime: ${deltaTime}`);
          }

          if (!isUnmounted.current) {
            const stores = getStores({ productData });
            const countries = getCountries({ productData, legacyI18n, IsDomesticSimplifiedChineseEnabled });
            if (isFunction(onLoadingDone)) {
              onLoadingDone({ stores, countries });
            }
          }
        },
        onError: (err) => {
          if (!isUnmounted.current) {
            if (isFunction(onLoadingError)) {
              onLoadingError(err);
            }
          }
        },
      });
  }, [i18n, legacyI18n, scope.values, isLoading, onLoadingDone, onLoadingError, IsDomesticSimplifiedChineseEnabled, isOdataGetProductScopeEnabled, serverContext]);

  const classes = useDefaultStyles(getStoreStyles);

  return (
    <div className={classNames('store-settings-loading', classes.storeSettings, isLoading && classes.storeSettingsLoading)}>
      {isEmpty(loadingErrors) &&
        <>
          { onRenderCustomLoadingComponent ? onRenderCustomLoadingComponent() : <Loading text={i18n.getString(_TL_('Loading...'))} isVisible /> }
          {alwaysShowError && campaignObjective === 'ShoppingCampaign' &&
            <Alert
              message={i18n.getString(_TL_('Cannot continue until stores are loaded.'))}
              level="Error"
              icon={(<div className="alert-image" />)}
              dismissible={false}
            />
          }
        </>
      }
      {!isEmpty(loadingErrors) &&
        <Alert
          message={i18n.getString(_TL_('Cannot continue because stores failed to load. Please try again or contact the customer support.'))}
          level="Error"
          icon={(<div className="alert-image" />)}
          dismissible={false}
        />
      }
    </div>
  );
};

StoreSelectLoading.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  legacyI18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      uid: PropTypes.number.isRequired,
    }),
  }).isRequired,
  permissions: PropTypes.shape({
    IsDomesticSimplifiedChineseEnabled: PropTypes.bool,
  }).isRequired,
  campaignObjective: PropTypes.string.isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({}).isRequired,
    alwaysShowError: PropTypes.bool,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      TimeZone: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func,
  }),
  onLoadingDone: PropTypes.func,
  onLoadingError: PropTypes.func,
  loadingErrors: PropTypes.arrayOf({}),
  onRenderCustomLoadingComponent: PropTypes.func,
};

StoreSelectLoading.defaultProps = {
  onLoadingDone: undefined,
  onLoadingError: undefined,
  loadingErrors: [],
  onRenderCustomLoadingComponent: null,
  activity: null,
};

export const ValidatedStoreSelectLoading = withControlErrorHandling(withUnvalidatedControlErrors(withCustomValidation(withPermissions(withScope(withServerContext(withActivityContext(StoreSelectLoading)))))));
